import type { TFunction } from "i18next";
import {
  decodeApiDuration,
  formatDurationSeconds,
  type DurationUnitLabels,
} from "src/utils/formatDashboardValue";
import {
  formatNumberDigits,
  isArabicLanguage,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import type { TabId } from "../types/Tabs";

/**
 * Bidi isolate for a bare numeral run (thousands separators, a leading sign): without it the
 * group can be reordered inside an RTL paragraph.
 *
 * Percentages deliberately do *not* get this treatment — see `formatMetricPercent`.
 */
const asNumericToken = (text: string, language: string): string =>
  isArabicLanguage(language) ? wrapBidiIsolate(text) : text;

export function formatMetricCount(value: number, language: string): string {
  return asNumericToken(formatNumberDigits(value, {}, language), language);
}

/**
 * Left unisolated on purpose. A trailing `%` is a bidi ET, which binds to Western (EN) digits
 * but not to Eastern Arabic-Indic (AN) ones, so in Arabic it resolves against the RTL paragraph
 * and renders on the *left* of the digits (`%٤٥.٣٤`) — the Arabic convention, and what the
 * Sales & Products dashboard shows. Isolating would force LTR order and push the `%` back to the
 * right, directly against the trend arrow.
 */
export function formatMetricPercent(
  value: number,
  language: string,
  fractionDigits = 2
): string {
  return `${formatNumberDigits(
    value,
    { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits },
    language
  )}%`;
}

/** Signed change percentage, e.g. `+12.30%` / `-٤.٠٥%`; `0` renders without a sign. */
export function formatSignedMetricPercent(value: number, language: string): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const digits = formatNumberDigits(
    Math.abs(value),
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    language
  );
  return `${sign}${digits}%`;
}

const durationUnitLabels = (t: TFunction): DurationUnitLabels => ({
  minute: t("webAnalytics.duration.minute"),
  minutes: t("webAnalytics.duration.minutes"),
  second: t("webAnalytics.duration.second"),
  seconds: t("webAnalytics.duration.seconds"),
});

export function formatMetricDuration(
  totalSeconds: number,
  t: TFunction,
  language: string
): string {
  return formatDurationSeconds(totalSeconds, durationUnitLabels(t), count =>
    formatMetricCount(count, language)
  );
}

/**
 * Formats a metric value for display. Keyed off the tab id rather than its label so the
 * logic is unaffected by the localized metric names the API returns.
 */
export function formatMetricValue(
  tabId: TabId,
  value: number,
  t: TFunction,
  language: string
): string {
  if (tabId === "bounce-rate") {
    return formatMetricPercent(value, language, 2);
  }
  if (tabId === "session-duration") {
    return formatMetricDuration(decodeApiDuration(value), t, language);
  }
  return formatMetricCount(value, language);
}

const UNKNOWN_DIMENSION_VALUES = new Set(["unknown", "(not set)", "not set", "other", "(other)"]);

/** Capitalizes only the first character, leaving acronyms like "Smart TV" untouched. */
export function capitalizeLabel(label: string): string {
  return label.length > 0 ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

const dimensionSlug = (value: string): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * GA4 reports these dimensions as a fixed, always-English enum (`male`, `desktop`,
 * `Organic Search`, …), so they are display labels rather than tenant data and are
 * localized from a lookup — same approach as `formatOrganicPostTypeLabel`. Anything
 * outside the enum is passed through untouched so unrecognized source values are
 * never rewritten.
 */
export type WebAnalyticsDimension = "gender" | "device" | "channel";

const DIMENSION_VALUE_SLUGS: Record<WebAnalyticsDimension, ReadonlySet<string>> = {
  gender: new Set(["male", "female", "unknown"]),
  device: new Set(["desktop", "mobile", "tablet", "smart_tv", "smarttv", "wearable", "console"]),
  channel: new Set([
    "direct",
    "organic_search",
    "paid_search",
    "organic_social",
    "paid_social",
    "organic_shopping",
    "paid_shopping",
    "organic_video",
    "paid_video",
    "display",
    "email",
    "referral",
    "affiliates",
    "audio",
    "sms",
    "cross_network",
    "mobile_push_notifications",
    "unassigned",
  ]),
};

export function formatDimensionValue(
  dimension: WebAnalyticsDimension,
  rawValue: string,
  t: TFunction
): string {
  const raw = String(rawValue ?? "").trim();
  if (UNKNOWN_DIMENSION_VALUES.has(raw.toLowerCase())) {
    return t("webAnalytics.map.unknown");
  }

  const slug = dimensionSlug(raw);
  if (slug && DIMENSION_VALUE_SLUGS[dimension].has(slug)) {
    return t(`webAnalytics.dimensionValues.${dimension}.${slug}`, {
      defaultValue: capitalizeLabel(raw),
    });
  }

  return capitalizeLabel(raw);
}

/**
 * Age buckets are numeric ranges (`18-24`, `65+`), so only the digits are localized —
 * apart from the "unknown" bucket, which is a word.
 */
export function formatAgeBucketLabel(rawValue: string, t: TFunction, language: string): string {
  const raw = String(rawValue ?? "").trim();
  if (UNKNOWN_DIMENSION_VALUES.has(raw.toLowerCase())) {
    return t("webAnalytics.map.unknown");
  }
  return isArabicLanguage(language)
    ? asNumericToken(toArabicIndicDigitsInString(raw), language)
    : capitalizeLabel(raw);
}
